import { useState, useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { personalInfo } from "@/mocks/devData";

gsap.registerPlugin(ScrollTrigger);

export default function DevHero() {
  const [roleIndex, setRoleIndex] = useState(0);
  const canvasRef = useRef(null);
  const sectionRef = useRef(null);
  const leftRef = useRef(null);
  const statusRef = useRef(null);
  const nameRef = useRef(null);
  const typeRef = useRef(null);
  const roleTextRef = useRef(null);
  const bioRef = useRef(null);
  const ctaRef = useRef(null);
  const statsRef = useRef(null);

  // Modern blur/slide crossfade between roles instead of a letter-by-letter typewriter
  useEffect(() => {
    const interval = setInterval(() => {
      if (!roleTextRef.current) return;
      gsap.to(roleTextRef.current, {
        opacity: 0,
        y: -12,
        filter: "blur(6px)",
        duration: 0.35,
        ease: "power2.in",
        onComplete: () => {
          setRoleIndex((i) => (i + 1) % personalInfo.roles.length);
        },
      });
    }, 2600);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!roleTextRef.current) return;
    gsap.fromTo(
      roleTextRef.current,
      { opacity: 0, y: 12, filter: "blur(6px)" },
      {
        opacity: 1,
        y: 0,
        filter: "blur(0px)",
        duration: 0.5,
        ease: "power2.out",
      },
    );
  }, [roleIndex]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);
    const particles = Array.from({ length: 60 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: 0.8 + Math.random() * 1.8,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      alpha: 0.1 + Math.random() * 0.25,
    }));
    let animId;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(22,163,74,${p.alpha * 0.6})`;
        ctx.fill();
      });
      particles.forEach((a, i) => {
        particles.slice(i + 1).forEach((b) => {
          const dist = Math.hypot(a.x - b.x, a.y - b.y);
          if (dist < 100) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(22,163,74,${0.03 * (1 - dist / 100)})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        });
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
      tl.fromTo(
        statusRef.current,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.7 },
      )
        .fromTo(
          nameRef.current,
          { opacity: 0, y: 60 },
          { opacity: 1, y: 0, duration: 0.9 },
          "-=0.3",
        )
        .fromTo(
          typeRef.current,
          { opacity: 0, x: -40 },
          { opacity: 1, x: 0, duration: 0.6 },
          "-=0.4",
        )
        .fromTo(
          bioRef.current,
          { opacity: 0, y: 25 },
          { opacity: 1, y: 0, duration: 0.6 },
          "-=0.3",
        )
        .fromTo(
          ctaRef.current,
          { opacity: 0, y: 20, scale: 0.95 },
          { opacity: 1, y: 0, scale: 1, duration: 0.5 },
          "-=0.2",
        )
        .fromTo(
          statsRef.current,
          { opacity: 0, y: 20 },
          { opacity: 1, y: 0, duration: 0.5 },
          "-=0.2",
        );

      if (sectionRef.current) {
        gsap.to(leftRef.current, {
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top top",
            end: "bottom top",
            scrub: 1.5,
          },
          y: -80,
          ease: "none",
        });
      }
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 64;
    window.scrollTo({ top, behavior: "smooth" });
  };

  return (
    <section
      id="home"
      ref={sectionRef}
      className="relative pt-8 min-h-[100dvh] flex items-center justify-center overflow-hidden"
      style={{
        background: `
          radial-gradient(at 20% 20%, rgba(22,163,74,0.05) 0px, transparent 50%),
          radial-gradient(at 80% 80%, rgba(22,163,74,0.04) 0px, transparent 50%),
          #000000
        `,
      }}
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
      />

      {/* Animated orbs */}
      <div
        className="orb-1 absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(22,163,74,0.05) 0%, transparent 65%)",
          filter: "blur(60px)",
        }}
      />
      <div
        className="orb-2 absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(22,163,74,0.035) 0%, transparent 65%)",
          filter: "blur(60px)",
        }}
      />

      {/* Corner accents */}
      <div className="absolute top-8 left-8 w-14 h-14 border-t border-l border-white/5 pointer-events-none" />
      <div className="absolute top-8 right-8 w-14 h-14 border-t border-r border-white/5 pointer-events-none" />
      <div className="absolute bottom-8 left-8 w-14 h-14 border-b border-l border-white/5 pointer-events-none" />
      <div className="absolute bottom-8 right-8 w-14 h-14 border-b border-r border-white/5 pointer-events-none" />

      <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 flex flex-col items-center text-center pt-14 pb-8 md:pt-20 md:pb-12">
        <div ref={leftRef} className="flex flex-col items-center w-full">
          <div
            ref={statusRef}
            className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-8"
            style={{
              background: "rgba(22,163,74,0.07)",
              border: "1px solid rgba(22,163,74,0.18)",
            }}
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-700 opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-600" />
            </span>
            <span className="text-green-300 text-xs font-semibold tracking-wider font-mono">
              Open to opportunities
            </span>
          </div>

          <h1
            ref={nameRef}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light text-white leading-none mb-6 tracking-tight"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Hi, I am
            <br />
            <span className="font-black text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-green-500">
              Hasibur Rahman Safin
            </span>
          </h1>

          <div
            ref={typeRef}
            className="flex items-center justify-center gap-2 mb-6 h-10 overflow-hidden"
          >
            <span className="text-gray-600 text-base font-mono">&lt;</span>
            <span
              ref={roleTextRef}
              className="text-lg md:text-xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-green-300 via-emerald-400 to-green-500"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              {personalInfo.roles[roleIndex]}
            </span>
            <span className="w-0.5 h-6 bg-green-600 animate-pulse" />
            <span className="text-gray-600 text-base font-mono">/&gt;</span>
          </div>

          <p
            ref={bioRef}
            className="text-gray-400 text-base leading-relaxed max-w-lg mx-auto mb-10"
          >
            I build fast, responsive, and SEO-friendly websites that help
            businesses establish a strong online presence, improve user
            experience, and turn visitors into customers.
          </p>

          <div
            ref={ctaRef}
            className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-10"
          >
            <button
              onClick={() => scrollTo("projects")}
              className="btn-3d btn-3d-primary px-5 py-2.5 text-xs sm:px-7 sm:py-3 sm:text-sm w-full sm:w-auto"
            >
              <i className="ri-code-box-line" /> View My Work
            </button>
            <button
              onClick={() => scrollTo("contact")}
              className="btn-3d btn-3d-outline px-5 py-2.5 text-xs sm:px-7 sm:py-3 sm:text-sm w-full sm:w-auto"
            >
              <i className="ri-send-plane-line" /> Get In Touch
            </button>
          </div>

          <div
            ref={statsRef}
            className="flex flex-col sm:flex-row items-center justify-center gap-6"
          >
            <div className="flex items-center gap-2">
              {[
                {
                  icon: "ri-github-fill",
                  href: personalInfo.github,
                  label: "GitHub",
                },
                {
                  icon: "ri-linkedin-box-fill",
                  href: personalInfo.linkedin,
                  label: "LinkedIn",
                },
                {
                  icon: "ri-mail-fill",
                  href: `mailto:${personalInfo.email}`,
                  label: "Email",
                },
              ].map((s) => (
                <a
                  key={s.icon}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={s.label}
                  className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:text-green-400 transition-all duration-200 cursor-pointer"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <i className={`${s.icon} text-base`} />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}